import { useEffect } from "react";

import { useTheme } from "@emotion/react";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import CollectionActivity from "@hl/shared-features/lib/features/marketplace/activity/CollectionActivity";
import { AppShell, Box, createStyles, Tabs } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate, useParams } from "react-router-dom";

import { MINT_URL } from "~config";
import { shloms404OnImageError } from "~features/MintPage/custom/Shloms404Carousel";
import { BrowseCollectionQuery } from "~features/browse/queries/browse.graphql.generated";
import MarketplacePageSkeleton from "~features/marketplace/skeleton/MarketplacePageSkeleton";
import { useMintPage } from "~hooks/useMintPage";
import { MintContext } from "~hooks/useMintState";

import { AppHeader } from "../layout/Header";

import MarketplaceHeader from "./MarketplaceHeader";
import { MarketplaceTokens } from "./MarketplaceTokens";
import { useMarketplaceBrowseCollectionQuery } from "./queries/browse.graphql.generated";

const useStyles = createStyles((theme) => ({
  link: {
    textDecoration: "none",
  },
  appShellMain: {
    paddingLeft: "20px !important",
    paddingRight: "20px !important",
    [theme.fn.largerThan(theme.breakpoints.md)]: {
      paddingLeft: "40px !important",
      paddingRight: "40px !important",
    },
  },
}));

export enum MintStatus {
  All = "all",
  Minted = "minted",
  Available = "available",
}

export type BrowseCollection = Pick<
  BrowseCollectionQuery["getPublicCollection"],
  | "address"
  | "name"
  | "description"
  | "reveal"
  | "seriesDetails"
  | "chainId"
  | "seriesAttributes"
>;

const MarketplacePage = () => {
  const { collectionId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);
  const apolloMintPageUpdate = useFeatureFlags(
    FEATURE_FLAGS.APOLLO_MINT_PAGE_UPDATE
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (navigate && apolloMintPageUpdate && collectionId) {
      navigate(
        MINT_URL.tokens.mintMarketplaceQuery.replace(
          ":collectionId",
          collectionId
        )
      );
    }
  }, [apolloMintPageUpdate, navigate, collectionId]);
  const { data, loading } = useMarketplaceBrowseCollectionQuery({
    variables: {
      collectionId: collectionId!,
      // metadata hidden automatically returns only minted attributes
      minted: true,
    },
    fetchPolicy: "cache-first",
    skip: !collectionId,
  });

  const browseCollection = data?.getPublicCollection;

  const mintPage = useMintPage(collectionId ?? "", { skipAuction: true });
  const {
    collectionLoading,
    collection,
    isSeriesMint,
    isCodeGenMint,
    chainId,
    address,
    isMarketplaceEnabledForCollectionChain,
    marketplaceId,
  } = mintPage;

  const { classes } = useStyles();

  if (!collectionId) {
    return <NotFound />;
  }

  if ((!browseCollection && loading) || collectionLoading) {
    return <MarketplacePageSkeleton />;
  }

  if (!browseCollection || !collection || !(isSeriesMint || isCodeGenMint)) {
    return <NotFound />;
  }

  return (
    <MintContext.Provider value={mintPage}>
      <AppSEO
        title={`${browseCollection.name} - Marketplace`}
        description={`Marketplace of ${browseCollection.name} tokens`}
      />
      <AppShell
        classNames={{
          main: classes.appShellMain,
        }}
        header={<AppHeader />}
      >
        <Box>
          <MarketplaceHeader mb={40} />
          <Tabs defaultValue="artwork" keepMounted={false}>
            <Tabs.List mb={isMobile ? 0 : 24}>
              <Tabs.Tab value="artwork">Artwork</Tabs.Tab>
              {isMarketplaceEnabledForCollectionChain && (
                <Tabs.Tab value="activity">Activity</Tabs.Tab>
              )}
            </Tabs.List>
            <Tabs.Panel value="artwork">
              <MarketplaceTokens
                collectionId={collectionId}
                browseCollection={browseCollection}
              />
            </Tabs.Panel>
            <Tabs.Panel value="activity" pt="xs">
              <CollectionActivity
                collectionId={collectionId}
                chainId={chainId}
                address={address}
                marketplaceId={marketplaceId}
                onImageError={
                  collection.flagVariations.shloms404UI
                    ? shloms404OnImageError
                    : undefined
                }
              />
            </Tabs.Panel>
          </Tabs>
        </Box>
      </AppShell>
    </MintContext.Provider>
  );
};

export default MarketplacePage;
